import { useEffect, useState } from 'react'
import { put, del } from 'aws-amplify/api';
import { Drawer, Box, SvgIcon, IconButton, CircularProgress, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { DataGridPremium, GridColDef, GridRowModesModel, GridRowModes, GridRowParams, useGridApiRef, GridEventListener, GridRowEditStopReasons, GridRowModel, GridCallbackDetails, GridPreProcessEditCellProps } from '@mui/x-data-grid-premium';
import StyledBox from '../../common/Components/StyledBox'
import { ReactComponent as CrossIcon } from '../../icons/cross.svg';
import { ReactComponent as CheckmarkIcon } from '../../icons/checkmark.svg';
import { ReactComponent as TrashIcon } from '../../icons/trash.svg';
import { toast } from "react-toastify";
import BoxHeader from '../../common/Components/BoxHeader';
import { ulid } from 'ulidx';
import { useFetch } from '../../hooks/useFetch';
import { SubscriptionItem } from '@aviation/cabinapp-audit-service-sdk';

export interface IAuditLogSettingsProps {
    open: boolean;
    cancelAction: () => void;
}

function AuditLogSettings(props: IAuditLogSettingsProps) {
    const apiRef = useGridApiRef();
    const { data = [] } = useFetch<Array<SubscriptionItem>>(`/api/audit/subscription/`, (response) => response);
    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [deleteItem, setDeleteItem] = useState<any | undefined>(undefined);
    const [rows, setRows] = useState<any[]>([]);

    useEffect(() => {
        setRows(data);
    }, [data]);

    const handleSaveClick = (item: any) => () => {
        setRowModesModel({ ...rowModesModel, [item.id ?? '']: { mode: GridRowModes.View } });
    };

    const handleCancelClick = (id: any) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });

        const editedRow = rows.find((row: any) => row.id === id);
        if ((editedRow as any)!.isNew) {
            const rowList = rows.filter((o: any) => o.id !== id)
            setRows(rowList);
        }
    };

    const handleRowModesModelChange = (model: GridRowModesModel, details: GridCallbackDetails) => {
        setRowModesModel(model);
        // eslint-disable-next-line
    };

    const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut)
            event.defaultMuiPrevented = true;
        else
            setRowModesModel({ ...rowModesModel, [params.id]: { mode: GridRowModes.View } });
    }

    const processRowUpdate = (row: GridRowModel) => {
        const updatedRow = { ...row, isNew: false, IsLoading: true };

        updateItem(row).then(success => {
            apiRef.current.updateRows([{ Id: row.Id, IsLoading: false }]);

            if (success)
                toast.success(`Changes have been saved successfully.`);
            else
                toast.error(`An error occurred while saving recipient`);
        })

        return updatedRow;
        // eslint-disable-next-line
    }

    const updateItem = async (item: any) => {
        let subItem = item as SubscriptionItem;

        try {
            await put({ path: `/api/audit/subscription`, apiName: 'api', options: { body: {...subItem}}});
            
            return true;
        } catch(e) {
            console.error(e);
            return false;
        } 
    }

    const handleCloseDialog = () => {
        setDeleteItem(undefined);
        setShowDeleteConfirmation(false);
    };

    const confirmDeletion = (item: any) => {
        setDeleteItem(item);
        setShowDeleteConfirmation(true);
    };

    const handleDeleteItem = async () => {
        setShowDeleteConfirmation(false);

        if (deleteItem !== undefined) {
            try {
                apiRef.current.updateRows([{ Id: deleteItem.Id, IsLoading: true }]);

                let response = await del({ path: `/api/audit/subscription/${deleteItem.Id}`, apiName: 'api'});
                let result = await response.response;
                
                let success = result.statusCode === 200;
                
                if (success) {
                    apiRef.current.updateRows([{ Id: deleteItem.Id, _action: 'delete' }]);
                    toast.success(`Recipient removed successfully.`);
                }
                else {
                    apiRef.current.updateRows([{ Id: deleteItem.Id, IsLoading: false }]);
                    toast.error(`An error occurred while removing recipient.`);
                }

                apiRef.current.updateRows([{ Id: deleteItem.Id, IsLoading: false }]);

            } catch(error) {
                console.error(error);
                apiRef.current.updateRows([{ Id: deleteItem.Id, IsLoading: false }]);
                toast.error(`An error occurred while saving recipient.`);
                return;
            } finally {
                setDeleteItem(undefined);
            }
            
        }
    };

    const createItem = async () => {
        let id = ulid().toString();
        let item = {
            Id: id,
            id : id, // Used by grid
            MailAdr: '',
            isNew: true
        };

        setRows([...rows, item]);
        apiRef.current.updateRows([item]);
        setRowModesModel((oldModel) => ({ ...oldModel, [id]: { mode: GridRowModes.Edit, fieldToFocus: 'Recipient' } }));
    }

    const columns: GridColDef[] = [
        {
            field: 'MailAdr', headerName: 'E-Mail Address', minWidth: 200, type: 'string', editable: true, preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                const address = params.props.value;
                const regEx = /^[a-zA-Z0-9.\-_]+@(?:[a-zA-Z0-9\-_]+\.)+[A-Za-z]+$/;
                const hasError = !regEx.test(address);
                return { ...params.props, error: hasError };
            }
        },
        {
            field: 'actions', type: 'actions', headerName: '', minWidth: 55, renderCell: ({ row }: Partial<GridRowParams>) => {
                const isInEditMode = (rowModesModel[row.id]?.mode ?? GridRowModes.View) === GridRowModes.Edit;
                const isLoading = row.IsLoading ?? false;

                if (isInEditMode)
                    return [
                        <IconButton color="success" onClick={handleSaveClick(row)}>
                            <SvgIcon component={CheckmarkIcon} inheritViewBox />
                        </IconButton>,
                        <IconButton color="error" onClick={handleCancelClick(row.id)}>
                            <SvgIcon component={CrossIcon} inheritViewBox />
                        </IconButton>
                    ]
                else if (!isInEditMode && !isLoading)
                    return [
                        <IconButton color="error" onClick={() => confirmDeletion(row)}>
                            <SvgIcon component={TrashIcon} inheritViewBox />
                        </IconButton>
                    ]
                else if (isLoading)
                    return (
                        <CircularProgress size={19} />
                    )
            }
        }
    ];

    return (
        <Drawer anchor='right' open={props.open} onClose={(e, r) => { props.cancelAction(); }} style={{ maxWidth: '50%' }}>
            <Box style={{ marginTop: 70, paddingLeft: 20, paddingRight: 20 }}>
                <BoxHeader title={'Recipients'}>
                    <Button variant="contained" onClick={createItem}>Create</Button>
                </BoxHeader>
            </Box>
            <StyledBox style={{ paddingLeft: 20, paddingRight: 20 }}>
                <DataGridPremium
                    apiRef={apiRef}
                    autoHeight
                    rows={rows}
                    columns={columns}
                    editMode="row"
                    getRowClassName={(params) => { return params.indexRelativeToCurrentPage % 2 === 0 ? `tui-grid-alternate-row` : '' }}
                    // pagination
                    isCellEditable={(params) => true}
                    getRowId={(itm) => itm.Id}
                    rowModesModel={rowModesModel}
                    onRowModesModelChange={handleRowModesModelChange}
                    onRowEditStop={handleRowEditStop}
                    processRowUpdate={processRowUpdate}
                />
            </StyledBox>

            <Dialog open={showDeleteConfirmation} onClose={handleCloseDialog}>
                <DialogTitle id="alert-dialog-title">
                    {"Delete recipient?"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Do you want to delete this recipient? You can't undo this action.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} autoFocus>Cancel</Button>
                    <Button onClick={handleDeleteItem} color="error">Delete</Button>
                </DialogActions>
            </Dialog>
        </Drawer>
    )
}

export default AuditLogSettings